import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../../config/queryKeys'
import { getTeamPlayerFinderSettings } from '../../../../services/team/playerFinderSettingsService'
import type {
  TeamPlayerFinderPayload,
  TeamPlayerFinderRegistration,
} from '../types'
import { playerFinderErrorStatus } from '../utils/playerFinderApiError'

const EMPTY_PAYLOAD: TeamPlayerFinderPayload = { registrations: [] }

interface UseTeamPlayerFinderSettingsOptions {
  teamId?: string | null
  /**
   * The authenticated user id — part of the cache key, because the entries are
   * the caller's own grants on the team.
   */
  authUserId?: string | number | null
  enabled?: boolean
}

/**
 * SN-4709 × SN-8081 — the Team page's Player Finder settings, and the Team page's
 * tab probe (one fetch: the page and the sub-tab share this key).
 *
 * A 403 (no authority on the team at all) resolves silently to no entries —
 * "no sub-tab", never a toast — matching the balance probes. Other errors
 * surface so the sub-tab can offer a retry. retry: false for the same reason the
 * balance probes use it: a 403 is an answer, not a transient failure.
 */
export const useTeamPlayerFinderSettings = ({
  teamId,
  authUserId,
  enabled = true,
}: UseTeamPlayerFinderSettingsOptions) => {
  const query = useQuery<TeamPlayerFinderPayload>({
    queryKey: queryKeys.teamPlayerFinder.byTeam(teamId, authUserId),
    queryFn: async () => {
      try {
        return await getTeamPlayerFinderSettings(teamId as string)
      } catch (error) {
        if (playerFinderErrorStatus(error) === 403) {
          return EMPTY_PAYLOAD
        }
        throw error
      }
    },
    enabled: Boolean(teamId) && enabled,
    retry: false,
    staleTime: 30_000,
  })

  const registrations: TeamPlayerFinderRegistration[] =
    query.data?.registrations ?? []

  return { ...query, registrations }
}

export default useTeamPlayerFinderSettings
